import React from 'react'
import { usePage } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import { FALLBACK_BRAND } from '@/lib/brand'
import type { SharedProps } from '@/types/shared'

export default function BaseRates({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  const brandProp = usePage().props.brand
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND
  // Room-Type Demand is globally kill-switched (App::RateEngine::Config); hide it here when off.
  const roomDemandEnabled = usePage().props.roomDemandEnabled === true
  const stageCount = roomDemandEnabled ? 13 : 12

  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Base Rates"
      subtitle="Your pricing anchor — every other adjustment builds on top of this."
    >
      <GuideSection title="Why Base Rates Matter">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The base rate is <strong className="text-foreground">stage 1 of the pipeline</strong>. Every
          seasonal boost, demand adjustment, and day-of-week modifier is a percentage of this number.
          It is the foundation that everything else builds on.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          If your base rate is too high, negative adjustments may not bring it down enough to fill
          rooms during low demand. If it is too low, positive adjustments will not reach your peak
          potential during high-demand periods. Getting the base rate right means the rest of the
          pipeline works as intended.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Think of the base rate as your{' '}
          <strong className="text-foreground">"average midweek, mid-season rate"</strong> — the
          natural resting price when nothing special is happening. No events, no seasonal boost, no
          weekend premium. Just a regular Tuesday in shoulder season. The pipeline takes it from there.
        </p>
      </GuideSection>

      <GuideSection title="Setting Base Rates">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Navigate to <strong className="text-foreground">Pricing Rules → Rooms Setup</strong>. You
          will see every room as a tree — independent rooms at the top level, any linked rooms
          nested beneath their parent. Click a room to open its editor, set its base rates (and
          optional floor/ceiling and lead plan), then press{' '}
          <strong className="text-foreground">Review &amp; Save</strong>. A summary of exactly what
          will change is shown before anything is written; confirming saves all rooms in one atomic
          step and triggers a recompute.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          You set a rate <strong className="text-foreground">per room type, per occupancy level</strong>.
          If your property uses occupancy-based pricing, a double room at 1-guest occupancy might be
          £90 while 2-guest occupancy is £110. This lets you capture the additional revenue from extra
          guests while remaining competitive for single travellers.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          If your property uses per-room pricing (same rate regardless of guest count), you set a
          single base rate per room type. The pricing mode is configured at the room type level.
        </p>
      </GuideSection>

      <GuideSection title="Independent Room Type Pricing">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Every room type runs through the{' '}
          <strong className="text-foreground">full {stageCount}-stage pipeline independently</strong>. Each
          room type has its own base rate, demand signals, and guardrails. This means a Sea View
          Suite can respond differently to demand than a Standard Double — because they genuinely
          have different booking patterns.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The <strong className="text-foreground">Property Demand</strong> stage ensures all room
          types respond to the overall booking pace
          {roomDemandEnabled ? (
            <>
              , while the{' '}
              <strong className="text-foreground">Room Demand</strong> stage fine-tunes each room type
              based on how it is performing relative to the property average. A room type that is
              filling faster gets a price lift; one that is lagging gets a discount.
            </>
          ) : (
            '.'
          )}
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each room type has its own{' '}
          <strong className="text-foreground">floor and ceiling prices</strong> (guardrails).
          Set these per room type to reflect the different value each room category offers.
        </p>
      </GuideSection>

      <GuideSection title="Linking a Room to a Parent (Optional)">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Independent pricing is the default and the right choice for most rooms. Optionally, a
          per-room room can be <strong className="text-foreground">linked to a parent</strong> so it
          always tracks the parent's computed price by a fixed difference — either an absolute
          amount (e.g. <strong className="text-foreground">+£20</strong>) or a percentage (e.g.{' '}
          <strong className="text-foreground">−10%</strong>). Negative values price the room below
          its parent.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A linked room skips the demand, event and gap-filling stages (those already shaped the
          parent's price) and instead applies the offset, then its own optional{' '}
          <strong className="text-foreground">floor and ceiling</strong>. It still follows the
          parent's manual overrides, and a manual override placed directly on the child always wins
          locally. Linking is <strong className="text-foreground">one level deep only</strong> — a
          parent cannot itself be linked, and per-occupancy rooms cannot be linked in this version.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The Rooms Setup tree shows each room's average rate and flags a{' '}
          <strong className="text-foreground">price inversion</strong> when a larger room ends up
          priced below a smaller one — a quick check that your ladder still makes sense.
        </p>
      </GuideSection>

      {/* COMBINED INVENTORY. Said once, on the page whose instructions it actually changes: prices
          are set at the lead listing, whose Rooms Setup lists the building's rooms (owner ruling
          2026-10-26), and a Same room's pairing and offset are set in the room mapping. Static copy,
          because the guides are property-agnostic: there is no combined inventory in scope to name. */}
      <GuideSection title="If Your Building Uses Combined Inventory">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When one building is listed several times in your PMS and set up as{' '}
          <strong className="text-foreground">combined inventory</strong>, base rates, floors, ceilings and the whole
          room ladder are set <strong className="text-foreground">at the lead listing</strong>. Its Rooms Setup lists
          the building&apos;s rooms once each, the way an ordinary hotel&apos;s does. Where two rooms from different
          listings share a name, the listing is added in brackets so you can tell them apart. A following
          listing&apos;s own pricing pages send you to the lead listing.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each room on a following listing is a <strong className="text-foreground">Same room</strong> or an{' '}
          <strong className="text-foreground">Added room</strong>. A Same room is the same physical rooms as a room at
          the lead listing: it sells at that room&apos;s price and has no base rate of its own, so Rooms Setup lists
          its lead room only, counting the building&apos;s rooms. An Added room is one the lead listing does not have:
          it is listed in Rooms Setup like any other room, where you set its base rate or have it follow a room. Each
          listing still publishes its own prices to the PMS and keeps its own live or paused switch. Removing a
          listing makes its rooms independent again, so any that took their price from a room elsewhere will need
          their own base rate.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          How a following listing sells the building&apos;s rooms is set on its{' '}
          <strong className="text-foreground">room mapping</strong> page: Business settings, Combined inventory,{' '}
          <strong className="text-foreground">Edit room mapping</strong> (Rooms Setup at the lead listing links there
          too). Each room is a Same room of one lead room or an Added room, and a Same room can have an{' '}
          <strong className="text-foreground">offset</strong> when you don&apos;t want both listings to show the same
          price: a percentage (the default) or an amount, above or below the lead room&apos;s price. With +10%, a Same
          room of a Double selling at {'£'}110 sells at {'£'}121, and moves whenever the Double does.{' '}
          <strong className="text-foreground">Same offset for every Same room</strong> sets one offset on all of them in
          one go. Restrictions and rooms left to sell still follow the lead room, and the offset is removed if the room
          becomes an Added room or its listing leaves.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The same page says what each rate plan of a Same room does. It{' '}
          <strong className="text-foreground">copies</strong> a rate plan of the lead room (matched by name for you), it{' '}
          <strong className="text-foreground">follows</strong> the Same room&apos;s own lead rate plan by an offset, the
          way a derived rate plan follows its lead, or it is not priced. A rate plan your PMS switches on later is
          matched by name when it can be. Every change is reviewed before it saves, kept in History and can be undone
          there. Owners and admins change everything; a manager who prices every listing can change the offsets. When you
          change the base rate, floor or ceiling of a lead room that a Same room copies, the review on Rooms Setup names
          each Same room it reaches, with its listing and its offset.
        </p>
      </GuideSection>

      <GuideSection title="How Base Rates Are Seeded">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When you onboard a property, {brand} reads your{' '}
          <strong className="text-foreground">current room rates from your PMS</strong> and uses them as
          initial base rates. This gives you a working starting point without having to enter every
          rate from scratch.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Rate bounds (floor and ceiling) are{' '}
          <strong className="text-foreground">auto-set at ±30%</strong> of the seeded rate. For
          example, if your PMS has your Standard Double at £100, {brand} sets the floor to £70 and the
          ceiling to £130.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          We recommend <strong className="text-foreground">reviewing and adjusting</strong> both the
          seeded base rates and the auto-generated bounds after onboarding. The imported rates may
          reflect a seasonal rate rather than your true midweek average, and the ±30% range may be too
          tight or too wide for your property.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Set your base rate to your typical midweek, mid-season rate. Let seasons and demand handle
            the peaks and troughs. This gives the pipeline the most room to work with.
          </GuideTip>
          <GuideTip type="do">
            Set base rates that reflect the genuine value difference between room types. A Sea View
            Suite should have a higher base than a Standard Double because guests value it more —
            not because of an arbitrary offset.
          </GuideTip>
          <GuideTip type="dont">
            Do not set base rates to your peak-season weekend rate and then use negative modifiers
            everywhere. This makes the pipeline harder to reason about and limits the upside — you
            cannot go higher than a rate that is already at peak.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'how-it-works', title: 'How It Works' },
          { slug: 'seasons', title: 'Seasons' },
          { slug: 'price-guardrails', title: 'Price Guardrails' },
        ]}
      />
    </GuideLayout>
  )
}
